<template>
  <div class="bg-white lg:bg-gray-50 rounded-lg border border-gray-200 p-6">
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Create New Item</h3>
    </div>

    <form @submit.prevent="createNewShoppingItem" class="space-y-6" novalidate>
      <FormErrorSummary :errors="errorSummary" />

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
        </label>
        <input
          v-model="newShoppingItem.name"
          type="text"
          placeholder="Item name"
          class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all text-[16px] sm:text-base"
          :class="nameDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
          @blur="touch('name')"
        />
        <p v-if="nameDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ nameDisplayError }}</p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <input
            v-model.number="newShoppingItem.quantity"
            type="number"
            min="0"
            step="0.1"
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all text-[16px] sm:text-base"
            :class="quantityDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('quantity')"
          />
          <p v-if="quantityDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ quantityDisplayError }}</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Unit <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <input
            v-model="newShoppingItem.unit"
            type="text"
            placeholder="kg, pcs, etc."
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all text-[16px] sm:text-base"
            :class="unitDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('unit')"
          />
          <p v-if="unitDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ unitDisplayError }}</p>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Total Price <span class="text-gray-400 font-normal">(optional)</span>
          </label>
          <div class="relative">
            <input
              v-model.number="newShoppingItem.price"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all text-[16px] sm:text-base"
            />
            <span class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
          </div>
          <p class="text-sm text-gray-500 mt-1">Enter the total price for this item (not per unit)</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Type/Category <span class="text-red-500 ml-0.5" aria-label="required">*</span>
          </label>
          <input
            v-model="newShoppingItem.type"
            type="text"
            placeholder="food, toiletries, etc."
            class="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all text-[16px] sm:text-base"
            :class="typeDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
            @blur="touch('type')"
          />
          <p v-if="typeDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ typeDisplayError }}</p>
        </div>
      </div>

      <button
        type="submit"
        class="w-full inline-flex items-center justify-center px-6 py-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:ring-4 focus:ring-primary-100 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="creating"
      >
        <svg v-if="creating" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        <span v-if="creating">Adding Item...</span>
        <span v-else>Add Item</span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { CREATE_SHOPPING_ITEM } from '../../graphql/mutations'
import { GET_SHOPPING_LIST } from '../../graphql/queries'
import { showError } from '../../utils/errorHandling'
import { useFormValidation } from '../../composables/useFormValidation'
import FormErrorSummary from '../FormErrorSummary.vue'
import { vm } from '../../utils/validationMessages'

interface Props {
  shoppingListId: string
  allIngredients: any[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'item-created': []
}>()

const newShoppingItem = ref({
  name: '',
  ingredient_id: '',
  quantity: 1,
  unit: '',
  price: 0,
  type: ''
})

const { mutate: createShoppingItem, loading: creating } = useMutation(CREATE_SHOPPING_ITEM, {
  update: (cache, { data }) => {
    if (data?.createShoppingItem && props.shoppingListId) {
      cache.modify({
        id: cache.identify({ __typename: 'ShoppingList', id: props.shoppingListId }),
        fields: {
          items(existingItems = []) {
            const newItemRef = cache.writeFragment({
              data: data.createShoppingItem,
              fragment: gql`
                fragment NewShoppingItem on ShoppingItem {
                    id
                    name
                    list_id
                    list_name
                    ingredient_id
                    quantity
                    unit
                    price
                    is_bought
                    type
                    created_at
                    updated_at
                }
              `
            })
            return [...existingItems, newItemRef]
          }
        }
      })
    }
  },
  refetchQueries: () => {
    const queries = []
    if (props.shoppingListId) {
      queries.push({ query: GET_SHOPPING_LIST, variables: { id: props.shoppingListId } })
    }
    return queries
  },
  awaitRefetchQueries: true
})

// Validation
const { submitAttempted, touch, displayError, attemptSubmit, reset: resetValidation } = useFormValidation()

const nameErr = computed(() => {
  if (!newShoppingItem.value.name.trim()) return vm.name.required
  return ''
})

const quantityErr = computed(() => {
  if (!newShoppingItem.value.quantity || newShoppingItem.value.quantity <= 0) return vm.quantity.positive
  return ''
})

const unitErr = computed(() => {
  if (!newShoppingItem.value.unit.trim()) return vm.unit.required
  return ''
})

const typeErr = computed(() => {
  if (!newShoppingItem.value.type.trim()) return vm.itemType.required
  return ''
})

const nameDisplayError = computed(() => displayError('name', nameErr.value))
const quantityDisplayError = computed(() => displayError('quantity', quantityErr.value))
const unitDisplayError = computed(() => displayError('unit', unitErr.value))
const typeDisplayError = computed(() => displayError('type', typeErr.value))

const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (nameErr.value) items.push({ field: 'name', label: 'Name', message: nameErr.value })
  if (quantityErr.value) items.push({ field: 'quantity', label: 'Quantity', message: quantityErr.value })
  if (unitErr.value) items.push({ field: 'unit', label: 'Unit', message: unitErr.value })
  if (typeErr.value) items.push({ field: 'type', label: 'Type/Category', message: typeErr.value })
  return items
})

const createNewShoppingItem = async () => {
  if (!props.shoppingListId) {
    showError('No shopping list available', 'create shopping item')
    return
  }

  if (!attemptSubmit({
    name: nameErr.value,
    quantity: quantityErr.value,
    unit: unitErr.value,
    type: typeErr.value,
  })) return

  try {
    await createShoppingItem({
      list_id: props.shoppingListId,
      name: newShoppingItem.value.name,
      ingredient_id: '',
      quantity: newShoppingItem.value.quantity,
      unit: newShoppingItem.value.unit,
      price: newShoppingItem.value.price,
      type: newShoppingItem.value.type
    })

    newShoppingItem.value = {
      name: '',
      ingredient_id: '',
      quantity: 1,
      unit: '',
      price: 0,
      type: ''
    }

    resetValidation()
    emit('item-created')
  } catch (error: any) {
    showError(error, 'create shopping item')
  }
}
</script>
